import React, { useState } from 'react';
import styled from 'styled-components';
import { UnstyledButton } from '../../Button';
import VipIcon from '../../../Icons/VipIcons';
import { getMediaType } from '../../MediaPlayer/exports';
import VideoThumbnailSelector from './VideoThumbnailSelector';
import LoadingSpinner from '../../../LoadingSpinner/LoadingSpinner';
import { MEDIA_GRID } from '../../../../constants/staticTexts';
import { formatMediaDuration } from '../../../../utils/formatters';

const MediaGrid = ({ 
  mediaItems, 
  messagePrice, 
  uploading,
  onRemoveItem,
  onToggleItemStatus,
  onUpdateItem,
  onAddMedia,
  maxFiles = 10,
  isEditing = false
}) => {
  const [thumbnailSelectorItem, setThumbnailSelectorItem] = useState(null);
  
  const hasPaidContent = parseFloat(messagePrice) > 0;
  // In edit mode, if there are any items with isFreePreview, show the lock UI
  // In create mode, only show lock UI if there's a price set
  const showLockUI = isEditing ? hasPaidContent && mediaItems.some(item => item.isFreePreview !== undefined) : hasPaidContent;
  const freeItems = mediaItems.filter(item => item.isFreePreview);
  const paidItems = mediaItems.filter(item => !item.isFreePreview);

  // Check if item has multiple thumbnail options (for videos)
  const hasMultipleThumbnails = (item) => {
    const mediaType = (item.mediaType || item.type || '').toLowerCase();
    return mediaType === 'video' && item.thumbnails && item.thumbnails.length > 1;
  };

  // Handle thumbnail selection for videos
  const handleThumbnailSelect = (item, index) => {
    if (onUpdateItem) {
      const selectedThumb = item.thumbnails[index];
      onUpdateItem(item.id, {
        selectedThumbnailIndex: index,
        thumbnailUrl: selectedThumb?.url,
        thumbnailS3Key: selectedThumb?.s3Key,
        displayUrl: selectedThumb?.url
      });
    }
    setThumbnailSelectorItem(null);
  };

  const handleItemClick = (e, item, index) => {
    e.preventDefault();
    e.stopPropagation();
    if (showLockUI) {
      onToggleItemStatus(item.id);
    }
  };

  const isProcessingItem = (item) => {
    const s = (item.processingStatus || '').toUpperCase();
    return s === 'PENDING' || s === 'PROCESSING';
  };

  const renderMediaPreview = (item) => {
    // Show processing spinner for media that hasn't finished server-side processing
    if (isProcessingItem(item)) {
      const mediaType = (item.mediaType || item.type || '').toLowerCase();
      if (mediaType === 'video') {
        return (
          <VideoFallback>
            <LoadingSpinner variant="logo" size={24} showText={true} text={MEDIA_GRID.PROCESSING} textSize={10} />
          </VideoFallback>
        );
      }
      return (
        <ImageFallback>
          <LoadingSpinner variant="logo" size={24} showText={true} text={MEDIA_GRID.PROCESSING} textSize={10} />
        </ImageFallback>
      );
    }

    // Priority order for media source:
    // 1. Selected thumbnail for videos with multiple options
    // 2. thumbnailUrl from server (after upload completion, especially for HEIC->JPEG conversion)
    // 3. Regular server URLs for uploaded files
    // 4. Local blob URLs for files being uploaded
    let mediaSrc = item.thumbnailUrl || item.displayUrl || item.fileUrl || item.url || item.src || item.preview;
    
    // For videos with multiple thumbnails, use the selected one
    if (hasMultipleThumbnails(item) && item.selectedThumbnailIndex !== undefined) {
      mediaSrc = item.thumbnails[item.selectedThumbnailIndex]?.url || mediaSrc;
    }
    
    // Use centralized media type detection
    let mediaType = (item.mediaType || item.type || '').toLowerCase();
    
    // If no explicit media type, use the centralized getMediaType utility
    if (!mediaType && (item.file || item.fileName)) {
      mediaType = getMediaType(item.file || { name: item.fileName || '', type: item.mimeType || '' });
    }
    
    const fileName = item.file?.name || item.fileName || '';
    
    switch (mediaType) {
      case 'image':
        // All images should display - browsers will handle what they can
        return (
          <UniversalImagePreview 
            src={mediaSrc} 
            alt={item.name || item.fileName}
            fileName={fileName}
            isUploading={item.uploading}
          />
        );
      case 'video':
        // For videos with thumbnails, show the thumbnail image instead of video element
        if (hasMultipleThumbnails(item) || item.thumbnailUrl) {
          return (
            <UniversalImagePreview 
              src={mediaSrc} 
              alt={item.name || item.fileName}
              fileName={fileName}
              isUploading={item.uploading}
            />
          );
        }
        // Fallback to video preview
        return (
          <UniversalVideoPreview 
            src={item.preview || item.fileUrl || item.url || item.src} 
            fileName={fileName}
            isUploading={item.uploading}
            muted 
          />
        );
      case 'voice':
      case 'audio':
        return (
          <UniversalAudioPreview 
            src={mediaSrc} 
            fileName={fileName}
            isUploading={item.uploading}
          />
        );
      default:
        return (
          <DefaultPreview>
            <VipIcon name="FileIcon" size={24} />
            <PlaceholderText>{MEDIA_GRID.FILE}</PlaceholderText>
          </DefaultPreview>
        );
    }
  };

  const renderStatusIndicator = (item, index) => {
    if (!showLockUI) return null;

    if (item.isFreePreview) {
      // Find the sequential number for this free item
      const freeIndex = mediaItems
        .slice(0, index + 1)
        .filter(mediaItem => mediaItem.isFreePreview)
        .length;
      return (
        <StatusIndicator $type="free">
          <NumberBadge>{freeIndex}</NumberBadge>
        </StatusIndicator>
      );
    } else {
      return (
        <StatusIndicator $type="paid">
          <LockIcon>
            <VipIcon name="Lock" size={14} />
          </LockIcon>
        </StatusIndicator>
      );
    }
  };

  if (mediaItems.length === 0) {
    return (
      <GridContainer>
        {uploading ? (
          <EmptyState>{MEDIA_GRID.UPLOADING_MEDIA}</EmptyState>
        ) : (
          <EmptyState>{MEDIA_GRID.NO_MEDIA_SELECTED}</EmptyState>
        )}
      </GridContainer>
    );
  }

  return (
    <GridContainer>
      <MediaGridLayout>
        {mediaItems.map((item, index) => (
          <MediaTile
            key={item.id}
            onClick={(e) => handleItemClick(e, item, index)}
            $clickable={showLockUI}
          >
            <MediaPreview>
              {renderMediaPreview(item)}
              {item.uploading && (
                <UploadProgress>
                  <ProgressBar $progress={item.progress || 0} />
                  <ProgressText>{item.progress || 0}%</ProgressText>
                </UploadProgress>
              )}
              {item.error && !item.uploading && (
                <ErrorOverlay>
                  <span>{MEDIA_GRID.UPLOAD_FAILED}</span>
                </ErrorOverlay>
              )}
              {(item.mediaType || item.type || '').toLowerCase() === 'video' && formatMediaDuration(item.duration) && (
                <DurationBadge>{formatMediaDuration(item.duration)}</DurationBadge>
              )}
              <RemoveButton onClick={(e) => {
                e.stopPropagation();
                onRemoveItem(item.id);
              }}>
                <VipIcon name="CloseXCircle" size={14} />
              </RemoveButton>
              {/* Video thumbnail selector button */}
              {hasMultipleThumbnails(item) && !item.uploading && !item.error && (
                <SelectCoverButton
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setThumbnailSelectorItem(item);
                  }}
                  title={MEDIA_GRID.SELECT_VIDEO_COVER}
                >
                  <VipIcon name="ImageIcon" size={10} />
                  <span>{MEDIA_GRID.COVER}</span>
                </SelectCoverButton>
              )}
            </MediaPreview>
            {renderStatusIndicator(item, index)}
          </MediaTile>
        ))}
        
        {/* Add Media Button */}
        {mediaItems.length < maxFiles && (
          <AddMediaTile onClick={onAddMedia}>
            <AddButton>
              <VipIcon name="Plus" size={16} />
            </AddButton>
          </AddMediaTile>
        )}
      </MediaGridLayout>

      {/* Status Message */}
      {showLockUI && mediaItems.length > 0 && (
        <StatusMessage>
          {MEDIA_GRID.TAP_TO_SET_FREE_PREVIEWS}
        </StatusMessage>
      )}

      {/* Summary */}
      {showLockUI && mediaItems.length > 0 && (
        <StatusSummary>
          <span>{freeItems.length} {MEDIA_GRID.FREE}</span>
          <span>•</span>
          <span>{paidItems.length} {MEDIA_GRID.LOCKED}</span>
        </StatusSummary>
      )}

      {/* Video thumbnail selection modal */}
      {thumbnailSelectorItem && (
        <VideoThumbnailSelector
          thumbnails={thumbnailSelectorItem.thumbnails}
          selectedIndex={thumbnailSelectorItem.selectedThumbnailIndex || 0}
          onSelect={(index) => handleThumbnailSelect(thumbnailSelectorItem, index)}
          onClose={() => setThumbnailSelectorItem(null)}
        />
      )}
    </GridContainer>
  );
};

// Styled Components
const GridContainer = styled.div`
  width: 100%;
`;

const MediaGridLayout = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 4px 0;
  scroll-behavior: smooth;
  
  /* Hide scrollbar but keep functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  
  &::-webkit-scrollbar {
    display: none; /* WebKit browsers */
  }

  @media (max-width: 768px) {
    gap: 6px;
  }

  @media (max-width: 480px) {
    gap: 4px;
  }
`;

const MediaTile = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  flex-shrink: 0;
  border-radius: 12px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.cardBg || '#2a2a2a'};
  border: 2px solid ${({ theme }) => theme.colors.border || '#404040'};
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  transition: all 0.2s ease;

  &:hover {
    transform: ${({ $clickable }) => $clickable ? 'scale(1.02)' : 'none'};
    border-color: ${({ theme, $clickable }) => 
      $clickable ? theme.colors.primary || '#007bff' : theme.colors.border || '#404040'};
  }

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }

  @media (max-width: 480px) {
    width: 90px;
    height: 90px;
  }
`;

const AddMediaTile = styled.div`
  width: 120px;
  height: 120px;
  flex-shrink: 0;
  border: 2px dashed ${({ theme }) => theme.colors.border || '#404040'};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ theme }) => theme.colors.cardBg || '#2a2a2a'};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary || '#007bff'};
    transform: scale(1.02);
  }

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }

  @media (max-width: 480px) {
    width: 90px;
    height: 90px;
  }
`;

const MediaPreview = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DurationBadge = styled.span`
  position: absolute;
  bottom: 4px;
  right: 4px;
  padding: 1px 5px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.3;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PreviewVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const DefaultPreview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 24px;
  color: ${({ theme }) => theme.colors.textSecondary || '#888'};
  gap: 8px;
`;

const PlaceholderText = styled.span`
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// Universal preview components that handle all media types
const UniversalImagePreview = ({ src, alt, fileName, isUploading }) => {
  const [imageError, setImageError] = React.useState(false);
  
  // If uploading, always show uploading state regardless of file type
  if (isUploading) {
    return (
      <ImageFallback>
        <LoadingSpinner variant="logo" size={24} showText={true} text={MEDIA_GRID.UPLOADING} textSize={10} />
      </ImageFallback>
    );
  }
  
  const handleImageError = () => {
    setImageError(true);
  };
  
  // If no src or error, show fallback
  if (imageError || !src) {
    return (
      <ImageFallback>
        <VipIcon name="ImageIcon" size={32} />
        <PlaceholderText>{MEDIA_GRID.IMAGE_FORMAT_NOT_SUPPORTED}</PlaceholderText>
      </ImageFallback>
    );
  }
  
  return (
    <PreviewImage 
      src={src} 
      alt={alt}
      onError={handleImageError}
      loading="lazy"
    />
  );
};

const UniversalVideoPreview = ({ src, fileName, isUploading, muted = true }) => {
  const [videoError, setVideoError] = React.useState(false);
  
  const handleVideoError = () => {
    setVideoError(true);
  };
  
  // If uploading, show uploading state
  if (isUploading) {
    return (
      <VideoFallback>
        <LoadingSpinner variant="logo" size={24} showText={true} text={MEDIA_GRID.UPLOADING} textSize={10} />
      </VideoFallback>
    );
  }
  
  if (videoError || !src) {
    // Fallback for unsupported video formats
    return (
      <VideoFallback>
        <VipIcon name="PlayIcon" size={32} />
        <PlaceholderText>{MEDIA_GRID.VIDEO_FORMAT_NOT_SUPPORTED}</PlaceholderText>
      </VideoFallback>
    );
  }
  
  return (
    <PreviewVideo 
      src={src} 
      muted={muted}
      onError={handleVideoError}
      preload="metadata"
    />
  );
};

const UniversalAudioPreview = ({ src, fileName, isUploading }) => {
  const [audioError, setAudioError] = React.useState(false);
  
  const handleAudioError = () => {
    setAudioError(true);
  };
  
  // If uploading, show uploading state
  if (isUploading) {
    return (
      <AudioPreviewContainer>
        <AudioVisual>
          <LoadingSpinner variant="logo" size={24} showText={true} text={MEDIA_GRID.UPLOADING} textSize={10} />
        </AudioVisual>
      </AudioPreviewContainer>
    );
  }
  
  return (
    <AudioPreviewContainer>
      {src && !audioError && (
        <HiddenAudio 
          src={src} 
          onError={handleAudioError}
          preload="metadata"
        />
      )}
      <AudioVisual>
        <VipIcon name="Music" size={24} />
        <PlaceholderText>{MEDIA_GRID.AUDIO_FORMAT_NOT_SUPPORTED}</PlaceholderText>
      </AudioVisual>
    </AudioPreviewContainer>
  );
};

// Styled components for universal previews
const ImageFallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.primary || '#007bff'}20, 
    ${({ theme }) => theme.colors.secondary || '#6c757d'}20);
  color: ${({ theme }) => theme.colors.textSecondary || '#888'};
  gap: 8px;
`;

const VideoFallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.danger || '#dc3545'}20, 
    ${({ theme }) => theme.colors.warning || '#ffc107'}20);
  color: ${({ theme }) => theme.colors.textSecondary || '#888'};
  gap: 8px;
`;

const AudioPreviewContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.success || '#28a745'}20, 
    ${({ theme }) => theme.colors.info || '#17a2b8'}20);
`;

const AudioVisual = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary || '#888'};
  gap: 8px;
`;

const HiddenAudio = styled.audio`
  display: none;
`;

const RemoveButton = styled(UnstyledButton)`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 1;

  &:hover {
    background: rgba(255, 0, 0, 0.8);
  }
`;

const SelectCoverButton = styled(UnstyledButton)`
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.75);
  color: white;
  border: none;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 9px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 3px;
  transition: all 0.2s ease;
  z-index: 5;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary || '#007bff'};
    transform: translateX(-50%) scale(1.05);
  }
`;

const StatusIndicator = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
`;

const NumberBadge = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary || '#007bff'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
`;

const LockIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AddButton = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary || '#888'};
  transition: color 0.2s ease;

  ${AddMediaTile}:hover & {
    color: ${({ theme }) => theme.colors.primary || '#007bff'};
  }
`;

const StatusMessage = styled.div`
  text-align: center;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary || '#888'};
  margin-bottom: 12px;
`;

const StatusSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary || '#888'};
  margin-bottom: 16px;

  span:nth-child(odd) {
    font-weight: 500;
  }
`;

const UploadProgress = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.bg3};
  color: white;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  overflow: hidden;
  
  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${({ $progress }) => $progress}%;
    background: ${({ theme }) => theme.colors.primary || '#28a745'};
    transition: width 0.3s ease;
  }
`;

const ProgressText = styled.span`
  font-size: 11px;
  font-weight: 600;
`;

const ErrorOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.danger || '#dc3545'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${({ theme }) => theme.colors.textSecondary || '#888'};
  font-size: 14px;
`;

export default MediaGrid;
